import { useEffect, useState } from 'react';
import { Sidebar, type ViewKey } from './components/shared/Sidebar';
import { Header } from './components/shared/Header';
import { KPISummary } from './components/KPISummary';
import { SupplierHealth } from './components/SupplierHealth';
import { WarehouseOps } from './components/WarehouseOps';
import { Transportation } from './components/Transportation';
import { Onboarding } from './components/Onboarding';
import { DemoWalkthrough } from './components/DemoWalkthrough';
import type { TenantResult } from './types';
import { getJSON } from './utils/api';

const META: Record<ViewKey, { title: string; subtitle: string }> = {
  kpis: { title: 'KPI Summary', subtitle: 'Top-line supply chain performance' },
  suppliers: { title: 'Supplier Health', subtitle: 'Reliability, quality, and risk' },
  warehouse: { title: 'Warehouse Operations', subtitle: 'Capacity and utilization' },
  transportation: { title: 'Transportation', subtitle: 'Shipments, carriers, and freight' },
  onboarding: { title: 'Onboarding', subtitle: 'Configure, map, and import your data' },
};

function loadTenant(): TenantResult | null {
  try {
    const raw = localStorage.getItem('jupiter.activeTenant');
    return raw ? (JSON.parse(raw) as TenantResult) : null;
  } catch {
    return null;
  }
}

export function App() {
  const [view, setView] = useState<ViewKey>('kpis');
  const [demoMode, setDemoMode] = useState(() => window.location.pathname === '/demo');
  const [refreshKey, setRefreshKey] = useState(0);
  const [activeTenant, setActiveTenant] = useState<TenantResult | null>(() => loadTenant());

  const meta = META[view];

  useEffect(() => {
    const onPopState = () => setDemoMode(window.location.pathname === '/demo');
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    if (activeTenant) return;
    getJSON<TenantResult>('/api/onboarding/tenants?slug=jupiter-demo')
      .then(activateTenant)
      .catch(() => {
        // A clean database may not be seeded yet; onboarding remains available.
      });
  }, [activeTenant]);

  function leaveDemo(target: ViewKey = 'kpis') {
    window.history.pushState({}, '', '/');
    setDemoMode(false);
    setView(target);
  }

  if (demoMode) {
    return (
      <DemoWalkthrough
        onExit={() => leaveDemo('kpis')}
        onExploreAcmeDashboard={() => leaveDemo('kpis')}
      />
    );
  }

  function activateTenant(tenant: TenantResult) {
    setActiveTenant(tenant);
    localStorage.setItem('jupiter.activeTenant', JSON.stringify(tenant));
  }

  return (
    <div className="flex h-full">
      <Sidebar active={view} onSelect={setView} />
      <div className="flex min-w-0 flex-1 flex-col">
        <Header
          title={meta.title}
          subtitle={activeTenant ? `${activeTenant.name} · ${meta.subtitle}` : meta.subtitle}
          onRefresh={view === 'onboarding' ? undefined : () => setRefreshKey((k) => k + 1)}
        />
        <main className="flex-1 overflow-y-auto p-6" key={`${view}-${refreshKey}`}>
          {view === 'kpis' && <KPISummary />}
          {view === 'suppliers' && <SupplierHealth tenantId={activeTenant?.tenant_id ?? null} />}
          {view === 'warehouse' && <WarehouseOps />}
          {view === 'transportation' && <Transportation />}
          {view === 'onboarding' && (
            <Onboarding onNavigate={setView} onTenantActivated={activateTenant} />
          )}
        </main>
      </div>
    </div>
  );
}
